// MA-858 (operator ruling D-1008-28) — the activation cloud merge, moved
// verbatim out of App.jsx's cloud-merge effect (behaviour-neutral) so a second
// caller can run the SAME merge: the /parent/kids card of a kid who is not the
// active kid (D-1008-27) merges that kid's cloud row before it proposes a level
// or saves an age. App.jsx calls this inside
// `writeProgressForChild(activeChildId, (prev) => ...)` exactly as before and
// still dispatches every push itself.
//
// Pure: no React, no storage, no network. `prev` is the progress writer's base
// for `localChildId`; `cloudStudent` is that kid's students row; `registry` is
// the local kids registry (planAgeOnMerge reads it).
//
// Returns { progress, pushes: { age, autoRead, mascot, levels, assigned, suppressed },
//           changed: { assigned, suppressed } } — the merged record, the
// pending pushes App.jsx used to stamp into its refs (null = nothing to push),
// and whether the assigned / suppressed sets changed (App.jsx invalidates the
// mission query on either).
import { defaultsForAge } from "../engine/placementEngine.ts";
import { planAgeOnMerge } from "./agePullMerge.js";
import { planAutoReadOnMerge } from "./autoReadPullMerge.js";
import { planMascotOnMerge } from "./mascotPullMerge.js";
import { resolveAssignedSkillsPullMerge } from "./assignedSkillsPullMerge.js";
import { resolveSuppressedSkillsPullMerge } from "./suppressedSkillsPullMerge.js";
import { resolveLevelsPullMerge } from "./levelsPullMerge.js";

export function mergeCloudStudentIntoProgress({ prev, cloudStudent, localChildId, registry }) {
  // MA-395 (merge-prev-from-storage) + MA-831 — `prev` is the progress
  // writer's base for THIS merge's child (activeChildId of this effect, not
  // the live one): the child's STORED record overlaid on React state
  // (SkillTree writes assignedSkillIds / suppressedSkillIds + stamps + the
  // cloud mirror straight to localStorage), with any key whose last write
  // failed kept at React's newer value. The result is saved under this
  // child's key only; React state is touched only if it is still the live
  // child. See App/progressWriter.js.
  // MA-771 — a local age the cloud never received (progress.agePendingSync)
  // beats the stale cloud age and is re-pushed below; otherwise cloud wins.
  // Decision = the pure planAgeOnMerge; half bands are kept, never pushed.
  const agePlan = planAgeOnMerge({ prev, cloudStudent, localChildId, registry });
  const age = agePlan.patch.studentAge;
  const d = defaultsForAge(Number(age || 6));
  // If the cloud profile has a math level, the student already completed placement —
  // don't make them redo it just because localStorage was cleared.
  const alreadyPlaced = !!cloudStudent.math_level_id;
  const placementStatus = alreadyPlaced ? "complete" : prev.placementStatus;
  // MA-1024 — the pal follows MA-792's pending-sync rule: a local pick the
  // cloud never confirmed (progress.mascotPendingSync) is kept and re-pushed;
  // otherwise the cloud's mascot_id wins, so a pal picked on another device
  // reaches this one. Schema migration is independent: an absent `mascot_id`
  // column reads as undefined and leaves prev.mascot alone. Decision = the
  // pure planMascotOnMerge (App/mascotPullMerge.js).
  const mascotPlan = planMascotOnMerge({ prev, cloudStudent, localChildId });
  // MA-183 — auto-read explicit override. Schema migration independent,
  // same pattern as mascot_id: absent column -> undefined -> `??` falls
  // through and prev.autoReadEnabled is left alone.
  // MA-792 — a local value the cloud never received (progress.autoReadPendingSync)
  // beats the stale cloud value and is re-pushed below. The decision is
  // the pure planAutoReadOnMerge; App.jsx only applies patch + push.
  const autoReadPlan = planAutoReadOnMerge({ prev, cloudStudent, localChildId });
  // MA-395 — pull-side LWW stamp compare for assignedSkillIds (mirrors
  // the levels compare below): a teacher's RPC addition (migration
  // 0027, students.assigned_skill_ids) always reaches this device via
  // the raw cloud array regardless of which side "wins"; a parent's OWN
  // un-assign, made on a stamped device, now actually propagates
  // instead of surviving forever under the old union-only merge. See
  // App/assignedSkillsPullMerge.js's header for the full rationale.
  const assignedMerge = resolveAssignedSkillsPullMerge({
    localIds: prev.assignedSkillIds,
    localStamp: prev.assignedSkillIdsUpdatedAt,
    cloudIds: cloudStudent.assigned_skill_ids,
    cloudStamp: cloudStudent.assigned_skill_ids_updated_at,
  });
  const assignedPush = assignedMerge.shouldPushLocal
    ? {
        studentId: cloudStudent.id,
        assignedSkillIds: assignedMerge.assignedSkillIds,
        assignedSkillIdsUpdatedAt: assignedMerge.assignedSkillIdsUpdatedAt,
        cloudMirror: assignedMerge.assignedSkillsCloudMirror,
      }
    : null;
  // MA-395 (suppressed-skill-ids-cloud-sync slice) — pull-side LWW stamp
  // compare for suppressedSkillIds (mirrors assignedMerge above). No
  // second cloud writer exists for this field, so unlike assignedMerge
  // there is no cloudMirror to carry forward.
  const suppressedMergeRaw = resolveSuppressedSkillsPullMerge({
    localIds: prev.suppressedSkillIds,
    localStamp: prev.suppressedSkillIdsUpdatedAt,
    cloudIds: cloudStudent.suppressed_skill_ids,
    cloudStamp: cloudStudent.suppressed_skill_ids_updated_at,
  });
  // Cross-field disjointness guard (red-team-required fix): the assigned
  // and suppressed pull-merges resolve INDEPENDENTLY, each against its
  // own cloud column/stamp. Two near-simultaneous device pushes (one to
  // assigned_skill_ids, one to suppressed_skill_ids) driven by the SAME
  // local disjointness clear (SkillTree.tsx's toggleAssign/toggleSuppress)
  // can land such that a fresh pull observes a skill id in BOTH resolved
  // sets (e.g. one push's reconnect retry never fired before the tab
  // closed). Strip any such id from suppressedSkillIds -- a deliberate
  // ASSIGN always wins over a stale suppression, mirroring SkillTree.tsx's
  // own local convention for the same disjointness invariant.
  const dedupedSuppressedIds = suppressedMergeRaw.suppressedSkillIds.filter(
    (id) => !assignedMerge.assignedSkillIds.includes(id),
  );
  const conflictStripped = dedupedSuppressedIds.length !== suppressedMergeRaw.suppressedSkillIds.length;
  const suppressedMerge = {
    ...suppressedMergeRaw,
    suppressedSkillIds: dedupedSuppressedIds,
    changed: suppressedMergeRaw.changed || conflictStripped,
  };
  const suppressedPush = suppressedMerge.shouldPushLocal
    ? {
        studentId: cloudStudent.id,
        suppressedSkillIds: suppressedMerge.suppressedSkillIds,
        suppressedSkillIdsUpdatedAt: suppressedMerge.suppressedSkillIdsUpdatedAt,
      }
    : null;
  // MA-375 — pull-side LWW stamp compare (mirrors updateStudentLevelsSafe's
  // push-side guard): adopt cloud levels only when local isn't STRICTLY
  // newer, so a parent's offline level override survives the next boot
  // instead of being silently clobbered back to a stale cloud value.
  const levelsMerge = resolveLevelsPullMerge({
    localMathModuleId: prev.mathModuleId,
    localReadingModuleId: prev.readingModuleId,
    localLevelsUpdatedAt: prev.levelsUpdatedAt,
    cloudMathLevelId: cloudStudent.math_level_id,
    cloudReadingLevelId: cloudStudent.reading_level_id,
    cloudLevelsUpdatedAt: cloudStudent.levels_updated_at,
    fallbackMathModuleId: d.mathModuleId,
    fallbackReadingModuleId: d.readingModuleId,
  });
  const levelsPush = levelsMerge.shouldPushLocal
    ? {
        studentId: cloudStudent.id,
        mathLevelId: levelsMerge.mathModuleId,
        readingLevelId: levelsMerge.readingModuleId,
        levelsUpdatedAt: levelsMerge.levelsUpdatedAt,
      }
    : null;
  const progress = { ...prev, onboardingComplete: true, studentName: cloudStudent.name, studentAge: age, agePendingSync: agePlan.patch.agePendingSync, agePendingBaseCloud: agePlan.patch.agePendingBaseCloud, agePendingBaseStudentId: agePlan.patch.agePendingBaseStudentId, studentGrade: cloudStudent.grade, mathModuleId: levelsMerge.mathModuleId, readingModuleId: levelsMerge.readingModuleId, levelsUpdatedAt: levelsMerge.levelsUpdatedAt, placementStatus, ...mascotPlan.patch, ...autoReadPlan.patch, assignedSkillIds: assignedMerge.assignedSkillIds, assignedSkillIdsUpdatedAt: assignedMerge.assignedSkillIdsUpdatedAt, assignedSkillsCloudMirror: assignedMerge.assignedSkillsCloudMirror, suppressedSkillIds: suppressedMerge.suppressedSkillIds, suppressedSkillIdsUpdatedAt: suppressedMerge.suppressedSkillIdsUpdatedAt };
  return {
    progress,
    pushes: { age: agePlan.push, autoRead: autoReadPlan.push, mascot: mascotPlan.push, levels: levelsPush, assigned: assignedPush, suppressed: suppressedPush },
    changed: { assigned: assignedMerge.changed, suppressed: suppressedMerge.changed },
  };
}
